import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { motion, useReducedMotion } from "motion/react";
import "./styles.css";

type DownloadTarget = {
  label: string;
  platform: string;
  url: string;
};

const MINI_PROGRAM_URL = import.meta.env.VITE_MINI_PROGRAM_URL?.trim() ?? "";
const MINI_PROGRAM_QR_URL = import.meta.env.VITE_MINI_PROGRAM_QR_URL?.trim() ?? "";
const BRAND_APP_ICON = "/images/brand/shapemoto-icon-flat.png?v=2-flow-s";
const HERO_STEP_DURATION_MILLISECONDS = 2400;
const HERO_TABS = ["给你推荐", "看适配", "安装建议", "落地服务"];
const HERO_TAB_POSITIONS = [72, 177, 282, 387];
const HERO_PANEL_STEPS = [
  { title: "按车型和预算推荐", detail: "先把选择范围缩小", fitTitle: "你的车能不能装", footer: "已找到 3 个适合方向" },
  { title: "先确认车型适配", detail: "核对年份、版本和接口", fitTitle: "车型适配已确认", footer: "适配信息已更新" },
  { title: "安装方式与工时", detail: "提前看施工难度和周期", fitTitle: "预计安装约 2 小时", footer: "安装建议已生成" },
  { title: "门店与落地服务", detail: "把安装和售后一起安排", fitTitle: "成都门店可预约", footer: "可继续联系顾问" }
];

type HeroDynamicContentProps = {
  activeStep: number;
  prefersReducedMotion: boolean | null;
};

/** 每个标签展示不同的信息结构，让状态切换不仅停留在文字替换。 */
function HeroDynamicMainContent({ activeStep, prefersReducedMotion }: HeroDynamicContentProps) {
  const activePanelStep = HERO_PANEL_STEPS[activeStep];

  return (
    <motion.g
      key={`main-content-${activeStep}`}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: "easeOut" }}
    >
      <text x="306" y="286" fill="#202020" fontSize="18" fontWeight="700">{activePanelStep.title}</text>
      <text x="306" y="313" fill="#777777" fontSize="14">{activePanelStep.detail}</text>

      {activeStep === 0 ? (
        <>
          <circle cx="318" cy="347" r="9" fill="#f25a1f" />
          <path d="M314 347 L317 350 L322 344" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <rect x="340" y="340" width="118" height="8" rx="4" fill="#a9a9a9" />
          <rect x="340" y="368" width="146" height="7" rx="3.5" fill="#c8c8c8" />
          <rect x="340" y="394" width="116" height="7" rx="3.5" fill="#d0d0d0" />
        </>
      ) : null}

      {activeStep === 1 ? (
        <>
          <rect x="306" y="334" width="96" height="30" rx="15" fill="#e8f8ec" />
          <circle cx="323" cy="349" r="7" fill="#22bf4b" />
          <text x="338" y="354" fill="#168a35" fontSize="14" fontWeight="700">适配通过</text>
          <rect x="306" y="382" width="74" height="26" rx="7" fill="#f3f3f3" />
          <rect x="390" y="382" width="74" height="26" rx="7" fill="#f3f3f3" />
          <text x="325" y="400" fill="#555555" fontSize="13">年份</text>
          <text x="409" y="400" fill="#555555" fontSize="13">接口</text>
        </>
      ) : null}

      {activeStep === 2 ? (
        <>
          <line x1="320" y1="352" x2="438" y2="352" stroke="#d2d2d2" strokeWidth="3" />
          {[320, 379, 438].map((circleX, index) => (
            <g key={circleX}>
              <circle cx={circleX} cy="352" r="13" fill={index === 0 ? "#f25a1f" : "#eeeeee"} />
              <text x={circleX} y="357" fill={index === 0 ? "#ffffff" : "#666666"} fontSize="12" fontWeight="700" textAnchor="middle">{index + 1}</text>
            </g>
          ))}
          <text x="306" y="397" fill="#555555" fontSize="14">确认方案　施工安装　交付检查</text>
        </>
      ) : null}

      {activeStep === 3 ? (
        <>
          <rect x="306" y="334" width="92" height="32" rx="8" fill="#f25a1f" />
          <text x="352" y="355" fill="#ffffff" fontSize="14" fontWeight="700" textAnchor="middle">成都门店</text>
          <rect x="409" y="334" width="82" height="32" rx="8" fill="#f2f2f2" />
          <text x="450" y="355" fill="#333333" fontSize="14" fontWeight="700" textAnchor="middle">可预约</text>
          <rect x="306" y="387" width="146" height="7" rx="3.5" fill="#bdbdbd" />
          <rect x="306" y="407" width="104" height="7" rx="3.5" fill="#d2d2d2" />
        </>
      ) : null}
    </motion.g>
  );
}

/** 标签切换时同步替换辅助卡片结构。 */
function HeroDynamicSecondaryCard({ activeStep, prefersReducedMotion }: HeroDynamicContentProps) {
  const cardTitles = ["材质怎么选", "关键适配项", "安装步骤", "落地安排"];

  return (
    <motion.g
      key={`secondary-card-${activeStep}`}
      initial={prefersReducedMotion ? false : { opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.34, ease: "easeOut" }}
    >
      <text x="78" y="471" fill="#202020" fontSize="17" fontWeight="700">{cardTitles[activeStep]}</text>

      {activeStep === 0 ? (
        <>
          {[75, 123, 171, 219, 267].map((swatchX, index) => (
            <rect key={swatchX} x={swatchX} y="488" width="34" height="34" rx="4" fill={["#eeeeee", "#a7a7a7", "#f7f7f7", "#202020", "#d4d4d4"][index]} stroke="#8d8d8d" />
          ))}
          <rect x="71" y="484" width="42" height="42" rx="6" fill="none" stroke="#f25a1f" strokeWidth="3" />
          <rect x="340" y="494" width="100" height="7" rx="3.5" fill="#c6c6c6" />
          <rect x="340" y="516" width="70" height="7" rx="3.5" fill="#dddddd" />
        </>
      ) : null}

      {activeStep === 1 ? (
        <>
          {["年份", "版本", "接口"].map((label, index) => (
            <g key={label}>
              <rect x={78 + index * 91} y="490" width="78" height="32" rx="8" fill="#f3f3f3" />
              <circle cx={92 + index * 91} cy="506" r="6" fill="#22bf4b" />
              <text x={105 + index * 91} y="511" fill="#444444" fontSize="13">{label}</text>
            </g>
          ))}
          <text x="378" y="511" fill="#168a35" fontSize="14" fontWeight="700">全部通过</text>
        </>
      ) : null}

      {activeStep === 2 ? (
        <>
          <line x1="94" y1="506" x2="282" y2="506" stroke="#dedede" strokeWidth="4" />
          {[94, 188, 282].map((circleX, index) => (
            <g key={circleX}>
              <circle cx={circleX} cy="506" r="14" fill={index === 0 ? "#f25a1f" : "#eeeeee"} />
              <text x={circleX} y="511" fill={index === 0 ? "#ffffff" : "#555555"} fontSize="12" fontWeight="700" textAnchor="middle">{index + 1}</text>
            </g>
          ))}
          <rect x="340" y="494" width="92" height="28" rx="14" fill="#f3f3f3" />
          <text x="386" y="513" fill="#333333" fontSize="13" textAnchor="middle">约 2 小时</text>
        </>
      ) : null}

      {activeStep === 3 ? (
        <>
          {["咨询", "到店", "交付"].map((label, index) => (
            <g key={label}>
              <rect x={78 + index * 91} y="490" width="78" height="32" rx="8" fill={index === 0 ? "#fff0e9" : "#f3f3f3"} />
              <text x={117 + index * 91} y="511" fill={index === 0 ? "#c84a17" : "#444444"} fontSize="13" fontWeight="700" textAnchor="middle">{label}</text>
            </g>
          ))}
          <text x="378" y="511" fill="#555555" fontSize="14">按进度跟进</text>
        </>
      ) : null}

      <text x="541" y="514" fill="#202020" fontSize="25">›</text>
    </motion.g>
  );
}

function BrandLockup() {
  return (
    <span className="brand-lockup">
      <img src={BRAND_APP_ICON} alt="" aria-hidden="true" />
      <span><strong>SHAPE MOTO</strong><small>型动</small></span>
    </span>
  );
}

function HeroProductIllustration() {
  const prefersReducedMotion = useReducedMotion();
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveStep((currentStep) => (currentStep + 1) % HERO_TABS.length);
    }, HERO_STEP_DURATION_MILLISECONDS);

    return () => window.clearInterval(timer);
  }, [prefersReducedMotion]);

  const activeTabX = HERO_TAB_POSITIONS[activeStep];
  const activePanelStep = HERO_PANEL_STEPS[activeStep];

  return (
    <div className="hero-illustration-shell">
      <img
        className="hero-product-illustration"
        src="/images/brand/hero-product-lineart.svg"
        alt="顾问根据车型和使用需求协助选择产品、确认适配的插画"
        loading="eager"
        fetchPriority="high"
      />
      <img
        className="hero-product-foreground hero-product-foreground-person"
        src="/images/brand/hero-product-lineart.svg"
        alt=""
        aria-hidden="true"
      />
      <img
        className="hero-product-foreground hero-product-foreground-hand"
        src="/images/brand/hero-pointing-hand.svg"
        alt=""
        aria-hidden="true"
      />
      <img
        className="hero-product-foreground hero-product-foreground-car"
        src="/images/brand/hero-product-lineart.svg"
        alt=""
        aria-hidden="true"
      />
      <svg
        className="hero-vector-panel"
        viewBox="0 0 826 958"
        aria-hidden="true"
      >
        <rect x="42" y="62" width="560" height="700" rx="18" fill="#ffffff" stroke="#d9d9d9" strokeWidth="2" />

        <rect x="62" y="83" width="78" height="78" rx="14" fill="#ffffff" stroke="#e4e4e4" strokeWidth="1.5" />
        <image href="/images/brand/hero-product-outline.svg" x="72" y="96" width="58" height="52" preserveAspectRatio="xMidYMid slice" />
        <rect x="157" y="102" width="150" height="10" rx="5" fill="#b7b7b7" />
        <rect x="157" y="133" width="102" height="7" rx="3.5" fill="#c7c7c7" />
        <rect x="278" y="133" width="100" height="7" rx="3.5" fill="#c7c7c7" />
        <rect x="474" y="101" width="102" height="48" rx="11" fill="#171717" />
        <motion.g
          animate={prefersReducedMotion ? undefined : { x: [0, 7, 0] }}
          transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 1.2 }}
        >
          <text x="525" y="134" fill="#ffffff" fontSize="30" textAnchor="middle">→</text>
        </motion.g>

        <line x1="62" y1="178" x2="577" y2="178" stroke="#e1e1e1" strokeWidth="1.5" />
        {HERO_TABS.map((tab, index) => (
          <text
            key={tab}
            x={HERO_TAB_POSITIONS[index]}
            y="217"
            fill={activeStep === index ? "#171717" : "#696969"}
            fontSize="15"
            fontWeight={activeStep === index ? "700" : "500"}
          >{tab}</text>
        ))}
        <motion.rect
          y="230"
          width="76"
          height="3"
          rx="1.5"
          fill="#f25a1f"
          animate={{ x: activeTabX }}
          transition={{ type: "spring", stiffness: 230, damping: 25 }}
        />
        <line x1="62" y1="237" x2="577" y2="237" stroke="#e1e1e1" strokeWidth="1.5" />

        <rect x="62" y="257" width="214" height="160" rx="13" fill="#ffffff" stroke="#dedede" strokeWidth="1.5" />
        <image href="/images/brand/hero-product-outline.svg" x="74" y="272" width="190" height="132" preserveAspectRatio="xMidYMid slice" />
        <HeroDynamicMainContent activeStep={activeStep} prefersReducedMotion={prefersReducedMotion} />

        <rect x="62" y="438" width="515" height="116" rx="13" fill="#ffffff" stroke="#dedede" strokeWidth="1.5" />
        <HeroDynamicSecondaryCard activeStep={activeStep} prefersReducedMotion={prefersReducedMotion} />

        <rect x="62" y="574" width="515" height="102" rx="13" fill="#ffffff" stroke="#dedede" strokeWidth="1.5" />
        <motion.text
          key={`fit-title-${activeStep}`}
          x="78"
          y="607"
          fill="#202020"
          fontSize="17"
          fontWeight="700"
          initial={prefersReducedMotion ? false : { opacity: 0, x: 70 }}
          animate={{ opacity: 1, x: 78 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >{activePanelStep.fitTitle}</motion.text>
        <image href="/images/brand/hero-product-outline.svg" x="76" y="616" width="62" height="42" preserveAspectRatio="xMidYMid slice" />
        <rect x="154" y="626" width="104" height="7" rx="3.5" fill="#bdbdbd" />
        <rect x="154" y="648" width="82" height="7" rx="3.5" fill="#d0d0d0" />
        <rect x="304" y="626" width="122" height="7" rx="3.5" fill="#bdbdbd" />
        <rect x="304" y="648" width="96" height="7" rx="3.5" fill="#d0d0d0" />
        <motion.g
          initial={false}
          animate={{ opacity: [0.55, 1], scale: [0.8, 1] }}
          transition={{ duration: 0.42, delay: 0.1 }}
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
          key={activeStep}
        >
          <circle cx="526" cy="635" r="14" fill="#22bf4b" />
          <path d="M519 635 L524 640 L533 629" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </motion.g>
        <text x="552" y="643" fill="#202020" fontSize="25">›</text>

        <circle cx="72" cy="720" r="7" fill="#f25a1f" />
        <motion.text
          key={`footer-${activeStep}`}
          x="88"
          y="727"
          fill="#222222"
          fontSize="17"
          fontWeight="650"
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.42 }}
        >{activePanelStep.footer}</motion.text>
      </svg>
    </div>
  );
}

const appDownloadTargets: DownloadTarget[] = [
  {
    label: "App Store",
    platform: "iPhone / iPad",
    url: import.meta.env.VITE_IOS_APP_URL?.trim() ?? ""
  },
  {
    label: "Android",
    platform: "Android 设备",
    url: import.meta.env.VITE_ANDROID_APP_URL?.trim() ?? ""
  }
];

const experienceHighlights = [
  { number: "01", title: "先看设计方向", description: "先看车型轮廓和风格方向，判断是否符合你的想法。" },
  { number: "02", title: "再聊你的需求", description: "把车型、预算和怎么用车告诉顾问，他会接着和你聊。" },
  { number: "03", title: "手机上继续跟进", description: "小程序看案例，型动 App 提交需求、收消息。" }
];

/**
 * 官网聚焦品牌表达和移动端入口分发，案例浏览与服务沟通由小程序和 App 承接。
 */
function LandingPage() {
  const [entryMessage, setEntryMessage] = useState("");

  /**
   * 手机端优先通过微信 URL Link 唤起小程序；未配置时给出明确的扫码提示。
   */
  function openMiniProgram() {
    if (!MINI_PROGRAM_URL) {
      setEntryMessage("小程序即将上线，你可以先提交咨询。");
      return;
    }

    window.location.assign(MINI_PROGRAM_URL);
  }

  /** 官网预约优先进入小程序；尚未配置小程序时仍可通过站内表单提交，不制造死按钮。 */
  function openAppointment() {
    if (MINI_PROGRAM_URL) {
      window.location.assign(MINI_PROGRAM_URL);
      return;
    }
    window.location.assign("/contact.html?topic=appointment");
  }

  return (
    <main className="landing-page">
      <header className="site-header">
        <a className="brand-link" href="#top" aria-label="返回 SHAPE MOTO 首页">
          <BrandLockup />
        </a>

        <nav className="site-nav" aria-label="首页导航">
          <a href="#experience">怎么开始</a>
          <a href="#download">手机入口</a>
          <button type="button" onClick={openMiniProgram} disabled={!MINI_PROGRAM_URL}>
            {MINI_PROGRAM_URL ? "进入小程序" : "小程序即将上线"}
          </button>
        </nav>
      </header>

      <section className="hero-section" id="top" aria-labelledby="landing-title">
        <div className="hero-copy">
          <p className="eyebrow"><span aria-hidden="true" /> SHAPE THE DRIVE</p>
          <h1 id="landing-title">
            <span>别急着买，</span>
            <span>先看适不适合<span className="mobile-title-line">你的车。</span></span>
          </h1>
          <p className="hero-description">
            把车型、预算和平时怎么开告诉我们，选什么、能不能装、怎么落地，我们一起捋清楚。
          </p>

          <div className="hero-actions">
            <button className="primary-action" type="button" onClick={openAppointment}>
              聊聊我的车
              <span aria-hidden="true">→</span>
            </button>
            <a className="secondary-action" href="#download">查看手机入口</a>
          </div>

          {entryMessage ? <p className="entry-message" role="status">{entryMessage}</p> : null}

          <dl className="hero-stats" aria-label="品牌信息">
            <div><dt>BASE</dt><dd>成都</dd></div>
            <div><dt>FOCUS</dt><dd>汽车改装咨询</dd></div>
            <div><dt>EST.</dt><dd>2025</dd></div>
          </dl>
        </div>

        <div className="hero-showcase" aria-label="合作品牌产品选择与车型适配示意">
          <HeroProductIllustration />
        </div>
      </section>

      <section className="experience-section" id="experience" aria-labelledby="experience-title">
        <div className="section-heading">
          <p className="eyebrow"><span aria-hidden="true" /> HOW IT WORKS</p>
          <h2 id="experience-title">先看方向，再聊需求，后面用手机跟进。</h2>
        </div>

        <div className="experience-grid">
          {experienceHighlights.map((highlight) => (
            <article key={highlight.number}>
              <span>{highlight.number}</span>
              <h3>{highlight.title}</h3>
              <p>{highlight.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="download-section" id="download" aria-labelledby="download-title">
        <div className="download-copy">
          <p className="eyebrow eyebrow-light"><span aria-hidden="true" /> MOBILE ACCESS</p>
          <h2 id="download-title">用手机继续跟进。</h2>
          <p>微信里看案例和动态；型动 App 提交需求、收消息、看进度。</p>
          <img className="download-mark" src={BRAND_APP_ICON} alt="" />
        </div>

        <section className="mini-program-card" aria-labelledby="mini-program-title">
          <div className={`mini-program-code ${MINI_PROGRAM_QR_URL ? "has-code" : "is-pending"}`}>
            {MINI_PROGRAM_QR_URL ? (
              <img src={MINI_PROGRAM_QR_URL} alt="SHAPE MOTO 微信小程序码" />
            ) : (
              <div className="code-pending" role="status">
                <img src={BRAND_APP_ICON} alt="" />
                <strong>小程序即将上线</strong>
                <span>上线前可以先用联系表单咨询</span>
              </div>
            )}
          </div>
          <div className="mini-program-content">
            <small>WECHAT MINI PROGRAM</small>
            <h3 id="mini-program-title">微信里先看看</h3>
            <p>看案例和动态，也可以提交需求</p>
            <button type="button" onClick={openMiniProgram} disabled={!MINI_PROGRAM_URL}>
              {MINI_PROGRAM_URL ? "打开小程序" : "即将上线"}
              <span aria-hidden="true">{MINI_PROGRAM_URL ? "↗" : "·"}</span>
            </button>
          </div>
        </section>

        <section className="app-card" aria-labelledby="app-download-title">
          <div className="app-card-heading">
            <img src={BRAND_APP_ICON} alt="" />
            <div>
              <small>型动 APP</small>
              <h3 id="app-download-title">安装型动 App</h3>
            </div>
          </div>

          <div className="app-downloads">
            {appDownloadTargets.map((target) => target.url ? (
              <a key={target.label} href={target.url} rel="noreferrer" target="_blank">
                <span><small>{target.platform}</small><strong>{target.label}</strong></span>
                <b aria-hidden="true">↓</b>
              </a>
            ) : (
              <button key={target.label} type="button" disabled title={`${target.label} 下载地址待配置`}>
                <span><small>{target.platform}</small><strong>{target.label}</strong></span>
                <b>即将上线</b>
              </button>
            ))}
          </div>
        </section>
      </section>

      <footer className="landing-footer">
        <BrandLockup />
        <p>© 2026 SHAPE MOTO. SHAPE THE DRIVE.</p>
        <nav className="filing-links" aria-label="网站备案信息">
          <a href="/privacy.html">隐私政策</a>
          <a href="/terms.html">用户协议</a>
          <a href="/account-deletion.html">账号删除</a>
          <a href="https://beian.miit.gov.cn/" rel="noreferrer" target="_blank">蜀ICP备2025129796号-1</a>
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=51011502000962" rel="noreferrer" target="_blank">
            川公网安备51011502000962号
          </a>
        </nav>
      </footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<LandingPage />);
